import { $axios, $axiosOlap } from '@/api/$axios.js';
import { EBAY_ACCOUNT_HEADER } from '@/api/interceptors/channelAccountHeader.js';
import { messageQueryIssue } from '@/components/ebay/messages/ebayMessagesQuery.js';

// olap's eBay buyer-messages read endpoints (docs/ebay/MESSAGES_PLAN.md §2, unit D1 of §4).
// $axiosOlap does no camel/snake case conversion (unlike $axiosManager) and the interceptor
// stamps `Ebayaccountid` for every request under `/ebay/*` — see
// api/interceptors/channelAccountHeader.js — so these functions send plain camelCase bodies.
const BASE = '/api/analytics/ebay/messages';

// Reject invalid search input rather than silently reading an unfiltered mailbox.

// The opaque keyset cursor belongs to the selected server ordering. Optional entity,
// marketplace and conversation-type filters apply before pagination.
export const fetchEbayConversations = async ({
  filter,
  listingId,
  orderId,
  q,
  cursor,
  limit,
  sort,
  marketplaceId,
  conversationType,
} = {}) => {
  const body = { filter, limit };
  if (listingId != null) body.listingId = listingId;
  if (orderId != null) body.orderId = orderId;
  if (messageQueryIssue(q)) throw new RangeError('Search needs 2–200 characters.');
  if (q?.trim()) body.q = q.trim();
  if (sort && sort !== 'newest') body.sort = sort;
  if (marketplaceId) body.marketplaceId = marketplaceId;
  if (conversationType) body.conversationType = conversationType;
  if (cursor) body.cursor = cursor;
  const { data } = await $axiosOlap.post(`${BASE}/conversations`, body);
  return data;
};

// One conversation's full thread (as far as it is loaded — see `threadComplete`).
// `conversationId` is eBay-originated data traveling through our own path — encode it so a
// value containing `/` or `?` can't inject an extra path segment or start a query string.
// (encodeURIComponent leaves literal `.` characters alone, so something like `../x` still
// reads `..` in the encoded output — it's the `/` around it that gets neutralized, to
// `%2F`, which is what actually stops it from being read as a path separator.)
//
// `ebayAccountId` pins the read to one eBay account regardless of the page: the inbox leaves it
// out (the interceptor stamps the selected account on `/ebay/*` pages), while the MCP review
// card sits on the mutation queue — not under `/ebay/*` — and reads the account the request
// names. An explicit per-request `Ebayaccountid` wins over the interceptor's.
export const fetchEbayConversation = async (conversationId, { ebayAccountId, cursor, limit } = {}) => {
  const url = `${BASE}/conversations/${encodeURIComponent(conversationId)}`;
  const config = {};
  if (ebayAccountId != null && ebayAccountId !== '') config.headers = { [EBAY_ACCOUNT_HEADER]: String(ebayAccountId) };
  if (cursor || limit != null) config.params = { ...(cursor ? { cursor } : {}), ...(limit != null ? { limit } : {}) };
  const { data } = Object.keys(config).length ? await $axiosOlap.get(url, config) : await $axiosOlap.get(url);
  return data;
};

// The nav badge / Overview counts: {unanswered, unread, oldestUnansweredAt}.
// `ebayAccountId` pins the count to one eBay account, for callers outside `/ebay/*` (the
// Global Overview) where the interceptor stamps no account header; same rule as
// fetchEbayConversation above.
export const fetchEbayMessagesSummary = async ({ ebayAccountId } = {}) => {
  const url = `${BASE}/summary`;
  const { data } = ebayAccountId != null && ebayAccountId !== ''
    ? await $axiosOlap.get(url, { headers: { [EBAY_ACCOUNT_HEADER]: String(ebayAccountId) } })
    : await $axiosOlap.get(url);
  return data;
};

// The ETL's reply routes (wiseppc-etl-ebay docs/API_REFERENCE.md, "Buyer-message replies",
// unit D2 of the plan's §4), under the V2 base URL — `${VITE_API_BASE_URL_V2}/ebay-messages`,
// the same pattern as the eBay connect actions in stores/account.js. `$axios`'s interceptor
// stamps the Bearer token and `Businessprofileid`; unlike olap, the ETL reads the eBay account
// from the body / query (`ebayAccountId`), not from the `Ebayaccountid` header. It speaks
// camelCase and `$axios` converts nothing, so bodies and answers travel verbatim. Failures
// nest the stable code at `error.response.data.detail.errorCode`.
const ETL_BASE = '/ebay-messages';
const v2 = () => ({ baseURL: import.meta.env.VITE_API_BASE_URL_V2 });

// A reply is a queued mutation: this answers 202 `{ mutationId, status, replayed }`, and the
// outcome is read back with `fetchEbayReply`. `idempotencyKey` is what makes a retry safe —
// the same key with the same text replays the saved mutation instead of sending twice.
export const postEbayReply = async ({ ebayAccountId, conversationId, text, idempotencyKey }) => {
  const { data } = await $axios.post(
    `${ETL_BASE}/replies`,
    { ebayAccountId, conversationId, text, idempotencyKey },
    v2(),
  );
  return data;
};

// {mutationId, status, conversationId, text, createdAt, completedAt,
//  item: {status, resultEntityId, errorCode, errorIds, errorMessage}}.
export const fetchEbayReply = async (mutationId) => {
  const { data } = await $axios.get(`${ETL_BASE}/replies/${encodeURIComponent(mutationId)}`, v2());
  return data;
};

// {replies: [...]} of the same shape, newest first, from the last 7 days — so a reload can
// still show a pending / unknown reply. Also the cheapest way to learn replies are switched
// off (503 MESSAGE_REPLIES_DISABLED) before the seller has typed anything.
export const fetchEbayReplies = async ({ ebayAccountId, conversationId }) => {
  const { data } = await $axios.get(`${ETL_BASE}/replies`, {
    ...v2(),
    params: { ebayAccountId, conversationId },
  });
  return data;
};

// Synchronous, reversible, writers only → {conversationId, read}. Never triggered by a reply.
export const markEbayConversationRead = async (conversationId, { ebayAccountId, read }) => {
  const { data } = await $axios.post(
    `${ETL_BASE}/conversations/${encodeURIComponent(conversationId)}/read`,
    { ebayAccountId, read },
    v2(),
  );
  return data;
};

// Loads the thread's older pages live from eBay → {messagesAdded, threadComplete}. May mark
// the conversation read on eBay (unconfirmed), which is why the UI says so.
export const refreshEbayConversation = async (conversationId, { ebayAccountId }) => {
  const { data } = await $axios.post(
    `${ETL_BASE}/conversations/${encodeURIComponent(conversationId)}/refresh`,
    { ebayAccountId },
    v2(),
  );
  return data;
};
